import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { AttachmentPreviewList, useStagedFiles } from '@shared/intake';
import { quotationService } from '../services/quotationService';
import { useQuotationDraftStore, type IntakeSource } from '../store/quotationDraftStore';

const ACCEPTED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg,.xlsx,.xlsm,.csv';
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB matches the backend cache ceiling

interface IntakeSourceFormProps {
  submitLabel: string;
  onSubmit: (source: IntakeSource) => void;
  disabled?: boolean;
  /** Clear text/files after submit. Default true. Pass false to preserve drafts between submits. */
  resetOnSubmit?: boolean;
}

// Unified intake form — paste text and/or a screenshot, and/or upload one or more files.
// Everything is extracted together as one deduped set (86d3hdy52).
//
// Routing on submit:
//   * 0 or 1 file (text optional)  → onSubmit({ text, files })         → in-place extraction on the current draft
//   * 2+ files (text optional)     → createDraftFromFiles + navigate    → creates a fresh server draft (CSC-283 Layer 3)
//
// The single-source branch runs the combined /api/intake/extract call in-place via the
// store. The 2+-files branch creates a separate draft because it also persists the files
// as attachments and mints a Draft row server-side; pasted text is forwarded as the body
// so it isn't dropped.
export function IntakeSourceForm({ submitLabel, onSubmit, disabled, resetOnSubmit = true }: IntakeSourceFormProps) {
  const navigate = useNavigate();
  const loadDraft = useQuotationDraftStore((s) => s.loadDraft);

  const [text, setText] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const { staged, rawFiles, hasFiles, addFiles, addFromClipboard, removeAt, clear } =
    useStagedFiles({ maxSize: MAX_FILE_SIZE, onReject: toast.error });

  const busy = disabled || isCreatingDraft;

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (busy) return;
      addFiles(e.dataTransfer.files);
    },
    [addFiles, busy],
  );

  const hasText = text.trim().length > 0;
  const isMultiFile = staged.length > 1;
  const canSubmit = !busy && (hasText || hasFiles);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    // Multi-file branch — separate code path that persists attachments + mints a server
    // draft. Pasted text rides along as the body so it's extracted with the files.
    if (isMultiFile) {
      setIsCreatingDraft(true);
      try {
        const saved = await quotationService.createDraftFromFiles(
          rawFiles.map((file) => ({ file, includeInExtraction: true })),
          text,
        );
        loadDraft(saved);
        toast.success(
          `Draft ${saved.quotationNumber ?? ''} created with ${saved.lineItems.length} line item${saved.lineItems.length === 1 ? '' : 's'}`,
        );
        // Select the new draft in the workspace (the sole surface, 86d3k9589). When this
        // form is already mounted inside the workspace, updating ?id= re-selects in place
        // (no page change); from anywhere else it lands on the workspace with the draft open.
        navigate(`/quotations/workspace?id=${saved.serverId}`);
        if (resetOnSubmit) {
          setText('');
          clear();
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to create draft from files';
        toast.error(msg);
      } finally {
        setIsCreatingDraft(false);
      }
      return;
    }

    // Single-source branch (text-only OR exactly one file) — extracted together in-place.
    onSubmit({ text, files: rawFiles });
    if (resetOnSubmit) {
      setText('');
      clear();
    }
  }, [canSubmit, clear, isMultiFile, loadDraft, navigate, onSubmit, rawFiles, resetOnSubmit, text]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Paste Text or Image</Label>
          <Textarea
            placeholder="Paste client email, chat, or request… or paste a screenshot (Ctrl/⌘+V)"
            className="min-h-[240px] flex-1 resize-y font-mono text-sm"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={(e) => {
              if (busy) return;
              if (addFromClipboard(e)) {
                e.preventDefault();
                toast.success('Screenshot added');
              }
            }}
            disabled={busy}
          />
        </div>

        <div className="flex flex-col space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Upload File(s) — multiple supported
          </Label>
          <div
            className={cn(
              'flex min-h-[240px] flex-1 flex-col rounded-lg border-2 border-dashed transition-colors',
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-muted-foreground/50',
              busy && 'pointer-events-none opacity-60',
            )}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
          >
            <label className="flex flex-1 cursor-pointer flex-col items-center justify-center gap-3 p-6">
              <Upload className="h-10 w-10 text-muted-foreground/50" />
              <div className="text-center">
                <p className="text-sm font-medium">Drop file(s) here or click to upload</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Multiple files allowed · PDF, PNG, JPG, XLSX, XLSM, CSV (max 25 MB each)
                </p>
              </div>
              <input
                type="file"
                multiple
                accept={ACCEPTED_EXTENSIONS}
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  if (e.target.files) addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <AttachmentPreviewList items={staged} onRemove={removeAt} disabled={busy} />
        </div>
      </div>

      {isMultiFile && (
        <p className="text-center text-xs text-muted-foreground">
          {staged.length} files selected — they'll be extracted together into a fresh draft
          {hasText ? ', with your pasted text included.' : '.'}
        </p>
      )}

      <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full">
        {isCreatingDraft ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creating draft…
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-4 w-4" />
            {submitLabel}
          </>
        )}
      </Button>
    </div>
  );
}
